<template>
  <rmr-modal
    :value="value"
    variant="form"
    :icon="icon"
    eyebrow="Rapport Marque"
    :title="selectedReport ? selectedReport.brand_name : ''"
    subtitle="Les valeurs importées restent intactes. Les overrides prennent priorité quand ils sont renseignés."
    max-width="860"
    @input="$emit('input', $event)"
    @close="$emit('close')"
  >
    <template #body>
      <v-card-text v-if="selectedReport" class="pa-6">
        <div class="mid-metric-sections">
          <div
            v-for="metric in editableMetrics"
            :key="metric.key"
            class="mid-metric-section"
            :class="{ 'mid-metric-section--active': hasValue(overrideForm[metric.overrideField]) }"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="mid-metric-section__title">
                <span class="mid-metric-section__label">{{ metric.label }}</span>
                <rmr-status-chip
                  v-if="hasValue(overrideForm[metric.overrideField])"
                  label="Manuel"
                  variant="success"
                  size="sm"
                />
                <rmr-status-chip
                  v-else
                  label="Importée"
                  variant="neutral"
                  size="sm"
                />
              </div>
              <div class="mid-metric-section__meta">
                <span class="mid-metric-section__imported">
                  Importée : {{ formatMetricDisplay(getImportedValue(selectedReport, metric.importedField)) }}
                </span>
                <rmr-icon-button
                  v-if="hasValue(overrideForm[metric.overrideField])"
                  :icon="trashIcon"
                  variant="delete"
                  size="xs"
                  tooltip="Supprimer la valeur manuelle"
                  @click="$emit('clear-field', metric.overrideField)"
                />
              </div>
            </div>
            <div class="mid-metric-section__inputs">
              <rmr-text-field
                :value="overrideForm[metric.overrideField]"
                type="number"
                :placeholder="formatMetricDisplay(getImportedValue(selectedReport, metric.importedField))"
                @input="$emit('update-field', { field: metric.overrideField, value: $event })"
              />
              <div class="mid-metric-section__effective">
                <span class="mid-metric-section__effective-label">Valeur finale</span>
                <span class="mid-metric-section__effective-value">{{ formatMetricDisplay(getEffectiveValueFromForm(metric)) }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </template>
    <template #footer>
      <v-card-actions class="px-6 py-4">
        <rmr-action-button label="Tout effacer" variant="ghost" size="sm" @click="$emit('clear-all')" />
        <v-spacer />
        <rmr-action-button label="Annuler" variant="ghost" size="sm" @click="$emit('close')" />
        <rmr-action-button
          label="Enregistrer"
          variant="primary"
          size="sm"
          :loading="savingOverrides"
          @click="$emit('save')"
        />
      </v-card-actions>
    </template>
  </rmr-modal>
</template>

<script>
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";

export default {
  name: "ImportedDataMetricOverridesModal",
  components: {
    RmrActionButton,
    RmrIconButton,
    RmrStatusChip,
    RmrTextField,
    RmrModal,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedReport: {
      type: Object,
      default: null,
    },
    editableMetrics: {
      type: Array,
      default: function() {
        return [];
      },
    },
    overrideForm: {
      type: Object,
      default: function() {
        return {};
      },
    },
    savingOverrides: {
      type: Boolean,
      default: false,
    },
    getImportedValue: {
      type: Function,
      required: true,
    },
    getEffectiveValueFromForm: {
      type: Function,
      required: true,
    },
    formatMetricDisplay: {
      type: Function,
      required: true,
    },
    icon: {
      type: String,
      default: "",
    },
    trashIcon: {
      type: String,
      default: "",
    },
  },
  methods: {
    hasValue(value) {
      return value !== "" && value !== null && value !== undefined;
    },
  },
};
</script>

<style scoped>
.mid-metric-sections {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mid-metric-section {
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.mid-metric-section--active {
  border-color: rgba(22, 163, 74, 0.2);
  background: linear-gradient(135deg, #f0fdf4 0%, rgba(240, 253, 244, 0.55) 100%);
}

.mid-metric-section__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mid-metric-section__label {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.mid-metric-section__meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mid-metric-section__imported {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}

.mid-metric-section__inputs {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mid-metric-section__effective {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.mid-metric-section__effective-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.mid-metric-section__effective-value {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  font-variant-numeric: tabular-nums;
}
</style>
